import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Loader2 } from 'lucide-react';
import Modal from '@shared/ui/Modal';
import LoadingList from '@shared/ui/LoadingList';
import CustomFieldEditModal from './CustomFieldEditModal';
import CustomFieldViewModal from './CustomFieldViewModal';
import api from '../../lib/api';
import { useCustomFieldsCache } from '../../contexts/CustomFieldsCacheContext';
import { getCustomFieldValueCount } from '../../services/customFieldsApi';
import { suppressRowClick } from '../shared/DataGrid';

interface CustomFieldBadgeProps {
  entityType: string;
  entityId: string;
  entityDisplayName: string;
  workspaceId?: string;
  className?: string;
  count?: number; // Pre-fetched count (optional, for performance)
  canEdit?: boolean; // Workspace permission: can user edit custom field values?
  variant?: 'badge' | 'button'; // Display variant
}

const CustomFieldBadge: React.FC<CustomFieldBadgeProps> = ({
  entityType,
  entityId,
  entityDisplayName,
  workspaceId,
  className = '',
  count, // Pre-fetched count
  canEdit = false, // Workspace permission (default to read-only for backwards compatibility)
  variant = 'badge' // Display variant
}) => {
  const cache = useCustomFieldsCache();
  const [showModal, setShowModal] = useState(false);
  const [customFieldCount, setCustomFieldCount] = useState(count ?? 0);
  const [loading, setLoading] = useState(count === undefined);
  const [cachedOrganizationId, setCachedOrganizationId] = useState<string | null>(null);
  const [isPrefetching, setIsPrefetching] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [loadingOrgId, setLoadingOrgId] = useState(true);
  
  // Update count if provided via props
  useEffect(() => {
    if (count !== undefined) {
      setCustomFieldCount(count);
      setLoading(false);
    }
  }, [count]);
  
  useEffect(() => {
    // Only fetch count if not provided via props
    if (count === undefined) {
      fetchCustomFieldCount();
    }
    fetchOrganizationId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId, workspaceId, count]);
  
  const fetchOrganizationId = async () => {
    if (!workspaceId) {
      setLoadingOrgId(false);
      return;
    }
    
    try {
      // Check cache first to avoid duplicate API calls
      let organizationId: string | null = null;
      const cachedWorkspace = cache.getWorkspace(workspaceId);
      
      if (cachedWorkspace) {
        organizationId = cachedWorkspace.owner_org_id;
      } else {
        // Every badge on a page mounts in the same tick and misses the cache
        // together, so without sharing the in-flight request N rows meant N
        // identical GET /workspaces/:id calls (Sentry FRONTEND-B, #1179).
        let pending = cache.getWorkspacePromise(workspaceId);
        if (!pending) {
          pending = api.get(`/workspaces/${workspaceId}`)
            .then((workspaceResponse) => {
              const workspaceData = workspaceResponse.data.data || workspaceResponse.data;
              cache.setWorkspace(workspaceId, workspaceData);
              return workspaceData;
            })
            .finally(() => cache.clearWorkspacePromise(workspaceId));
          cache.setWorkspacePromise(workspaceId, pending);
        }
        const workspaceData = await pending;
        organizationId = workspaceData.owner_org_id;
      }
      
      // Cache organization ID for modal use
      setCachedOrganizationId(organizationId);
    } catch (error) {
      console.error('Error fetching organization ID:', error);
    } finally {
      setLoadingOrgId(false);
    }
  };
  
  const fetchCustomFieldCount = async () => {
    // This function is no longer needed when count is provided via props
    // It's kept for backwards compatibility but does nothing
    setLoading(false);
  };
  
  const handleClose = () => {
    if (Date.now() - openedAtRef.current < 300) return;
    setShowModal(false);
    setIsOpening(false);
  };

  const handleSave = async () => {
    // Refresh the count after saving
    try {
      const newCount = await getCustomFieldValueCount(entityType, entityId, workspaceId);
      setCustomFieldCount(newCount);
    } catch (error) {
      console.error('Error refreshing custom field count:', error);
    }
  };

  const openedAtRef = React.useRef<number>(0);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    suppressRowClick();
    openedAtRef.current = Date.now();
    setShowModal(true);
    setIsOpening(true);
  };

  const handleLoadingChange = (loading: boolean) => {
    if (!loading) {
      setIsOpening(false);
    }
  };

  const handleMouseEnter = () => {
    // Pre-fetch data on hover for faster modal opening
    if (!isPrefetching && cachedOrganizationId && workspaceId) {
      setIsPrefetching(true);
      // Trigger pre-fetch by importing the API functions
      import('../../services/customFieldsApi').then(({ getCustomFieldDefinitions, getCustomFieldValues, getAvailableWorkspaces }) => {
        // Pre-fetch in background (results will be cached by React Query if used, or browser cache)
        Promise.all([
          getCustomFieldDefinitions(cachedOrganizationId, entityType, true).catch(() => null),
          getCustomFieldValues(entityType, entityId, workspaceId).catch(() => null),
          getAvailableWorkspaces(entityType, entityId).catch(() => null)
        ]);
      });
    }
  };
  
  // Button variant (consistent with NoteCountBadge button variant)
  if (variant === 'button') {
    return (
      <>
        <button
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          className={`btn btn-primary inline-flex items-center gap-2 ${className}`}
          title="Custom fields"
          disabled={loading || isOpening}
        >
          {isOpening ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Plus className="w-4 h-4" />
          )}
          <span>Fields</span>
          {!loading && customFieldCount > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold bg-white bg-opacity-20 rounded-full">
              {customFieldCount}
            </span>
          )}
        </button>
        
        {/* Portal modals to document.body to escape AG Grid stacking context */}
        {showModal && createPortal(
          loadingOrgId ? (
            // Show loading modal while fetching organization ID
            <Modal isOpen={true} title="Custom fields" onClose={handleClose} size="xl">
              <LoadingList rows={3} surface="plain" label="Loading custom fields" />
            </Modal>
          ) : canEdit ? (
            <CustomFieldEditModal
              isOpen={showModal}
              onClose={handleClose}
              entityType={entityType}
              entityId={entityId}
              entityDisplayName={entityDisplayName}
              workspaceId={workspaceId}
              cachedOrganizationId={cachedOrganizationId}
              onLoadingChange={handleLoadingChange}
              onSave={handleSave}
            />
          ) : (
            <CustomFieldViewModal
              isOpen={showModal}
              onClose={handleClose}
              entityType={entityType}
              entityId={entityId}
              entityDisplayName={entityDisplayName}
              workspaceId={workspaceId}
            />
          ),
          document.body
        )}
      </>
    );
  }

  // Badge variant (default) - Show count in circle when data exists, "+" when empty
  const hasData = !loading && customFieldCount > 0;
  return (
    <>
      <button
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        className={`
          flex items-center justify-center rounded-full transition-colors
          ${hasData
            ? 'w-6 h-6 bg-primary-700 dark:bg-primary-600 text-white text-xs font-bold hover:bg-primary-800 dark:hover:bg-primary-500'
            : 'w-6 h-6 p-0 hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary'
          }
          ${className}
        `}
        title="Custom fields"
        disabled={loading || isOpening}
      >
        {isOpening ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : hasData ? (
          <span>{customFieldCount}</span>
        ) : (
          <Plus className="h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
        )}
      </button>
      
      {/* Portal modals to document.body to escape AG Grid stacking context */}
      {showModal && createPortal(
        loadingOrgId ? (
          // Show loading modal while fetching organization ID
          <Modal isOpen={true} title="Custom fields" onClose={handleClose} size="xl">
            <LoadingList rows={3} surface="plain" label="Loading custom fields" />
          </Modal>
        ) : canEdit ? (
          // Contributors and admins can edit custom field values
          <CustomFieldEditModal
            isOpen={showModal}
            onClose={handleClose}
            entityType={entityType}
            entityId={entityId}
            entityDisplayName={entityDisplayName}
            workspaceId={workspaceId}
            cachedOrganizationId={cachedOrganizationId}
            onLoadingChange={handleLoadingChange}
            onSave={handleSave}
          />
        ) : (
          // Guests can only view custom field values (read-only)
          <CustomFieldViewModal
            isOpen={showModal}
            onClose={handleClose}
            entityType={entityType}
            entityId={entityId}
            entityDisplayName={entityDisplayName}
            workspaceId={workspaceId}
          />
        ),
        document.body
      )}
    </>
  );
};

export default CustomFieldBadge;

